
import Vue from "vue";
import { UserMood } from "@prestonly/preston-common";
import { UserStats } from "@/store/modules/user";
import moment from "moment";
import FireIcon from "@/components/base/FireIcon.vue";
import FrozenFireIcon from "@/components/base/FrozenFireIcon.vue";
import { FireState } from "@/types/streak";

type Day = {
  date: Date;
  isFromPreviousMonth: boolean;
  isFromFutureMonth: boolean;
  isFutureDate: boolean;
  isCurrentDate: boolean;
  // Part of the streak chain: a day with activity or a day a freeze covered.
  isStreakDay: boolean;
  isActiveDay: boolean;
  isFrozenDay: boolean;
  submittedMood: UserMood | null;
};

type Month = {
  name: string;
  year: number;
  days: Day[];
};

type MonthData = {
  currentMonth: moment.Moment;
  previousMonth: moment.Moment;
  futureMonth: moment.Moment;
  daysInMonth: number;
  firstWeekday: number;
  lastWeekday: number;
};

export default Vue.extend({
  name: "DashboardDesktopMoodCalendar",
  components: { FireIcon, FrozenFireIcon },
  data: () => ({
    activeMonthIndex: 2,
    weekdays: [1, 2, 3, 4, 5, 6, 0],
    FireState,
  }),
  computed: {
    moods(): UserMood[] {
      return this.$store.getters["user/getMoods"];
    },

    moodsMap(): Map<string, UserMood> {
      const map = new Map();
      this.moods.forEach((mood) => map.set(moment(mood.date).format("YYYY-MM-DD"), mood));

      return map;
    },

    stats(): UserStats {
      return this.$store.getters["user/getStats"];
    },

    statsMap(): Map<string, number> {
      const map = new Map();
      this.stats.dailyCorrectAnswers.forEach((stat) => map.set(stat.date, stat.count));
      return map;
    },

    frozenDays(): Set<string> {
      return new Set(this.stats.frozenDays || []);
    },

    monthsConfig(): Month[] {
      const calendarData: Month[] = [];

      for (let i = 2; i >= 0; i--) {
        const { currentMonth, firstWeekday, lastWeekday } = this.getDefaultMonthData(i);
        const days: Day[] = [];

        if (firstWeekday !== 1) this.addPreviousMonthDays(i, days);
        this.addMonthDays(i, days);
        if (lastWeekday !== 7) this.addFutureMonthDays(i, days);

        const monthData: Month = {
          name: moment(currentMonth).locale("en").format("MMMM").toLowerCase(),
          year: currentMonth.year(),
          days,
        };

        calendarData.push(monthData);
      }

      return calendarData;
    },

    activeMonth(): Month {
      return this.monthsConfig[this.activeMonthIndex];
    },
  },
  methods: {
    getDefaultMonthData(i: number): MonthData {
      const currentMonth = moment().subtract(i, "months");
      const previousMonth = moment(currentMonth).subtract(1, "months");
      const futureMonth = moment(currentMonth).add(1, "months");

      const daysInMonth = currentMonth.daysInMonth();

      const firstWeekday = currentMonth.startOf("month").isoWeekday();
      const lastWeekday = currentMonth.endOf("month").isoWeekday();

      return {
        currentMonth,
        previousMonth,
        futureMonth,
        daysInMonth,
        firstWeekday,
        lastWeekday,
      };
    },

    addPreviousMonthDays(i: number, days: Day[]): void {
      const { previousMonth, firstWeekday } = this.getDefaultMonthData(i);
      const daysInPreviousMonth = previousMonth.daysInMonth();

      for (let j = firstWeekday - 1; j > 0; j--) {
        const date = moment(previousMonth).date(daysInPreviousMonth - j + 1);
        const dayItem = this.getDayItem(date, "previous");

        days.push(dayItem);
      }
    },

    addFutureMonthDays(i: number, days: Day[]): void {
      const { futureMonth, lastWeekday } = this.getDefaultMonthData(i);

      for (let j = lastWeekday + 1; j <= 7; j++) {
        const date = moment(futureMonth).date(j - lastWeekday);
        const dayItem = this.getDayItem(date, "future");

        days.push(dayItem);
      }

      // add additional rows to keep equal height of the container
      const rows = Math.ceil(days.length / 7);
      if (rows < 6) {
        const lastDay = days[days.length - 1];
        for (let j = 1; j <= (6 - rows) * 7; j++) {
          const test = moment(lastDay.date).add(j, "day");
          const dayItem = this.getDayItem(test, "future");
          days.push(dayItem);
        }
      }
    },

    addMonthDays(i: number, days: Day[]): void {
      const { currentMonth, daysInMonth } = this.getDefaultMonthData(i);

      for (let day = 1; day <= daysInMonth; day++) {
        const date = moment(currentMonth).date(day);
        const dayItem = this.getDayItem(date);

        days.push(dayItem);
      }
    },

    getDayItem(date: moment.Moment, type: "previous" | "future" | null = null): Day {
      const mood = this.moodsMap.get(moment(date).format("YYYY-MM-DD")) || null;
      const isActiveDay = this.getDailyCorrectAnswers(date.toDate()) > 0;
      const isFrozenDay = !isActiveDay && this.frozenDays.has(date.format("YYYY-MM-DD"));

      return {
        date: date.toDate(),
        submittedMood: mood,
        isFromPreviousMonth: type === "previous",
        isFromFutureMonth: type === "future",
        isFutureDate: date.isAfter(moment(), "day"),
        isCurrentDate: date.isSame(moment(), "day"),
        isStreakDay: isActiveDay || isFrozenDay,
        isActiveDay,
        isFrozenDay,
      };
    },

    changeMonth(direction: "prev" | "next"): void {
      direction === "prev" ? this.activeMonthIndex-- : this.activeMonthIndex++;
    },

    getEmptyIcon(isFutureDate: boolean): string {
      return isFutureDate ? "question_mark" : "close";
    },

    getDailyCorrectAnswers(date: Date): number | string {
      const dateFormat = moment(date).format("YYYY-MM-DD");
      return this.statsMap.get(dateFormat) || "";
    },

    isDailyIcon(date: Date): boolean {
      const dateFormat = moment(date).format("YYYY-MM-DD");
      return this.statsMap.has(dateFormat) && this.statsMap.get(dateFormat) > 0;
    },
  },
});
